package com.neurobs.ccabserver.transcriptreview.states

import com.neurobs.ccab.CCABServer
import com.neurobs.ccab.casr.CasrDatabaseJSON.{ReviewedTestTranscriptJSON, ReviewedTestTranscriptRecordingJSON, TestTranscriptJSON, TestTranscriptRecordingJSON}
import com.neurobs.ccab.db.{ReviewedTestTranscriptData, TestTranscriptData}
import com.neurobs.errors._
import com.neurobs.util.TimeSeconds
import org.scalajs.dom.AudioContext

import scala.concurrent.ExecutionContext.Implicits.global

case class RecordingTranscriptReviewData
(
	transcript: TestTranscriptRecordingJSON,
	reviewedTranscripts: Seq[(ReviewedTestTranscriptData,ReviewedTestTranscriptRecordingJSON)],
	sourceAudio: SpeechAudioFileDataContents,
	audioSegment: RecordingSegment
)

/** Transcript data necessary for review organized into recordings */
case class TestTranscriptReviewData
(
	serverData: TestTranscriptServerData,
	recordings: Seq[RecordingTranscriptReviewData]
)
{
	/** Returns the time within the combined, trimmed audio segments for the test
	 * given a transcript time within one of the recordings
	 *
	 * @param recordingIndex
	 * @param transcriptTime Time from a transcript (relative to beginning of original recording)
	 * @return Time within combined, trimmed audio
	 */
	def combinedSegmentsTime(recordingIndex: Int, transcriptTime: TimeSeconds): TimeSeconds = {
		val res = recordings.lift(recordingIndex) map { recording =>
			val previousDuration = Range(0, recordingIndex).foldLeft(TimeSeconds(0))(_ + recordings(_).audioSegment.interval.size)
			transcriptTime - recording.audioSegment.interval.begin + previousDuration
		}
		res.getOrElse(TimeSeconds(0))
	}
}

object TestTranscriptReviewData {

	private def findAudioFile(transcript: TestTranscriptRecordingJSON, files: Seq[SpeechAudioFileDataContents]): Result[SpeechAudioFileDataContents] = {
		val localFilename = transcript.code()
		toResult(files.find(_.metadata.code == localFilename), noValueMessage = s"Unable to locate audio file '$localFilename' referenced in transcript.")
	}

	private def getReviewedTranscripts(transcript: TestTranscriptRecordingJSON, reviewed: Seq[(ReviewedTestTranscriptData, ReviewedTestTranscriptJSON)]): Result[Seq[(ReviewedTestTranscriptData,ReviewedTestTranscriptRecordingJSON)]] = {
		val res = reviewed map { t =>
			toResult(t._2.reviewedTestTranscriptRecordings.find(_.code() == transcript.code()).map((t._1, _)), noValueMessage = s"Unable to locate reviewed transcript recording '${transcript.c}'")
		}
		sequence(res)
	}

	private def makeRecordingData(transcript: TestTranscriptJSON, reviewed: Seq[(ReviewedTestTranscriptData, ReviewedTestTranscriptJSON)], files: Seq[SpeechAudioFileDataContents])(implicit context: AudioContext): Result[Seq[RecordingTranscriptReviewData]] = {
		val recordingResults = transcript.testTranscriptRecordings.sortBy(_.ro.value) map { r =>
			for {
				reviewedTranscript <- getReviewedTranscripts(r, reviewed)
				audio <- findAudioFile(r, files)
				segment = RecordingSegment.trimmed(audio.contents, r)
			} yield RecordingTranscriptReviewData(r, reviewedTranscript, audio, segment)
		}
		sequence(recordingResults)
	}

	private def parseTestTranscript(data: TestTranscriptData): Result[TestTranscriptJSON] = {
		data.transcript match {
			case None => Left("The test transcript was empty.")
			case Some(transcriptJson) =>
				addError(TestTranscriptJSON.fromJsonString(transcriptJson), errorPrefix = "Error parsing database test transcript json string")
		}
	}

	private def parseReviewedTranscript(data: Seq[ReviewedTestTranscriptData]): Result[Seq[(ReviewedTestTranscriptData,ReviewedTestTranscriptJSON)]] = {
		sequence(data.map(t => addError(ReviewedTestTranscriptJSON.fromJsonString(t.transcript).map((t, _)), errorPrefix = "Error parsing database reviewed test transcript json string")))
	}

	/** Parses transcript JSON and matches up transcripts and audio for each recording */
	def apply(serverData: TestTranscriptServerData)(implicit context: AudioContext): Result[TestTranscriptReviewData] = {
		for {
			transcript <- parseTestTranscript(serverData.transcript)
			reviewed <- parseReviewedTranscript(serverData.reviewedTranscripts)
			recordings <- makeRecordingData(transcript, reviewed, serverData.audioFiles)
		} yield {
			TestTranscriptReviewData(serverData, recordings)
		}
	}

	/** Retrieves data from the server and constructs (and validates) data necessary for review */
	def apply(selection: TestTranscriptSelection)(implicit server: CCABServer, context: AudioContext): FutureResult[TestTranscriptReviewData] = {
		TestTranscriptServerData(selection).resultFlatMap(apply)
	}
}